import { cn, getImageUrl } from '@/lib/utils';
import { ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { PropertyImage } from '@/types/property';

interface ListingGalleryProps {
  images: PropertyImage[];
  className?: string;
  aspectRatio?: 'square' | 'video' | '4/3';
  showNavigation?: boolean;
  showIndicators?: boolean;
  small?: boolean;
}

export default function ListingGallery({
  images,
  className,
  aspectRatio = '4/3', // Default aspect ratio
  showNavigation = true,
  showIndicators = true,
  small = false,
}: ListingGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const aspectRatioClasses = {
    'square': 'aspect-square',
    'video': 'aspect-video',
    '4/3': 'aspect-4/3',
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentImageIndex((prev) => {
        const newIndex = prev === 0 ? images.length - 1 : prev - 1;
        setIsImageLoading(true);
        setImageError(false);
        return newIndex;
      });
    }
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentImageIndex((prev) => {
        const newIndex = prev === images.length - 1 ? 0 : prev + 1;
        setIsImageLoading(true);
        setImageError(false);
        return newIndex;
      });
    }
  };

  // Generate indicators array with max 5 dots
  const getIndicators = () => {
    if (images.length <= 5) {
      // If 5 or fewer images, show all dots
      return images.map((_, idx) => idx);
    } else {
      // If more than 5 images, create a sliding window of dots
      const maxDots = 5;
      const halfWindow = Math.floor(maxDots / 2);

      // Start with showing the first 5 dots
      if (currentImageIndex < halfWindow + 1) {
        return [0, 1, 2, 3, 4];
      }

      // End with showing the last 5 dots
      if (currentImageIndex >= images.length - halfWindow - 1) {
        return [
          images.length - 5,
          images.length - 4,
          images.length - 3,
          images.length - 2,
          images.length - 1
        ];
      }

      // Middle case - create a centered window around current index
      return [
        currentImageIndex - 2,
        currentImageIndex - 1,
        currentImageIndex,
        currentImageIndex + 1,
        currentImageIndex + 2
      ];
    }
  };

  const renderPlaceholder = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-500">
      <ImageIcon className={cn("mb-2", small ? "h-6 w-6" : "h-8 w-8")} />
      <span className={cn("text-center", small ? "text-xs" : "text-sm")}>
        Image unavailable
      </span>
    </div>
  );

  return (
    <div
      className={cn(
        'relative group overflow-hidden',
        className
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className={cn(
        'relative border border-gray-200',
        aspectRatioClasses[aspectRatio]
      )}>

        {images.length > 0 ? (
          <>
            {isImageLoading && !imageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <ImageIcon className={cn(
                  "animate-pulse text-gray-400",
                  small ? "h-6 w-6" : "h-8 w-8"
                )} />
              </div>
            )}
            <img
              src={getImageUrl(images[currentImageIndex].original_url, {
                width: small ? 400 : 800,
                height: small ? 300 : 600,
                resize: 'cover',
                quality: 80
              }) || ''}
              alt={`Property image ${currentImageIndex + 1}`}
              className={cn(
                "absolute inset-0 w-full h-full object-cover transition-opacity duration-200",
                (isImageLoading || imageError) && "opacity-0"
              )}
              onLoad={() => {
                setIsImageLoading(false);
                setImageError(false);
              }}
              onError={() => {
                setIsImageLoading(false);
                setImageError(true);
              }}
            />
            {imageError && renderPlaceholder()}

            {/* Simple darkening overlay on hover */}
            <div className={cn(
              "absolute inset-0 transition-opacity duration-200 bg-black/10",
              isHovering ? "opacity-100" : "opacity-0"
            )} />
          </>
        ) : renderPlaceholder()}

        {showNavigation && images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size={small ? "icon-sm" : "icon"}
              className={cn(
                "absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white opacity-0 group-hover:opacity-90 hover:bg-black/60",
                small && "h-6 w-6"
              )}
              onClick={handlePrevImage}
            >
              <ChevronLeft className={cn("h-4 w-4", small && "h-3 w-3")} />
            </Button>
            <Button
              variant="ghost"
              size={small ? "icon-sm" : "icon"}
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white opacity-0 group-hover:opacity-90 hover:bg-black/60",
                small && "h-6 w-6"
              )}
              onClick={handleNextImage}
            >
              <ChevronRight className={cn("h-4 w-4", small && "h-3 w-3")} />
            </Button>
          </>
        )}
      </div>

      {/* Image counter badge */}
      {images.length > 1 && (
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
          {currentImageIndex + 1}/{images.length}
        </div>
      )}

      {showIndicators && images.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
          {getIndicators().map((index) => (
            <div
              key={index}
              className={cn(
                'rounded-full transition-colors',
                index === currentImageIndex
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/80',
                'h-1.5 w-1.5',
                'cursor-pointer'
              )}
              onClick={() => setCurrentImageIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}